/**
 * Centralized API configuration for the client
 * Handles both development (Vite proxy) and production (subdomain) scenarios
 */

// Get the API base URL from environment
const envApiUrl = import.meta.env.VITE_API_URL;

// API configuration
export const apiConfig = {
  // Base URL for API requests - always use /api prefix
  baseURL: envApiUrl || "/api",

  // Full API URL for cases where we need the complete URL
  fullApiUrl: envApiUrl || "/api",
};

/**
 * Build a complete API URL for a given endpoint
 * @param endpoint - The API endpoint (e.g., "/auth/login")
 * @returns The complete URL
 */
export function buildApiUrl(endpoint: string): string {
  // Remove leading slash if present to avoid double slashes
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;

  // Always use /api prefix regardless of environment
  const baseUrl = apiConfig.baseURL.endsWith("/")
    ? apiConfig.baseURL.slice(0, -1)
    : apiConfig.baseURL;

  return `${baseUrl}/api/${cleanEndpoint}`;
}

/**
 * Get the base URL for API requests
 * @returns The base URL with /api prefix
 */
export function getApiBaseUrl(): string {
  const baseUrl = apiConfig.baseURL.endsWith("/")
    ? apiConfig.baseURL.slice(0, -1)
    : apiConfig.baseURL;
  return `${baseUrl}/api`;
}

/**
 * Check if the current configuration supports the given API URL
 * @param url - The URL to check
 * @returns Whether the URL is supported
 */
export function isSupportedApiUrl(url: string): boolean {
  // Check if it matches our configured domain
  try {
    const urlObj = new URL(url);
    const baseUrlObj = new URL(apiConfig.fullApiUrl);
    return urlObj.hostname === baseUrlObj.hostname;
  } catch {
    return false;
  }
}
